import React, { useState } from 'react';
import useClassificationLabelHook from '../hooks/useClassificationLabelHook';
import Button from '../../../../../components/Atoms/Button.jsx';
import { useI18n } from '../../../../../i18n';
import AutoGroupPanel from './AutoGroupPanel';
import { canGroup } from '../utils/autoGroup';

/**
 * The class bar: one chip per class, carrying its keyboard shortcut, a colour
 * accent and its running count, plus the labeling progress.
 *
 * A chip assigns the whole multi-selection when there is one, otherwise the
 * previewed image — the same rule as the digit shortcuts, so there is a single
 * grid of buttons instead of the two that used to appear.
 */

/** Stable per-class accent from the chart tokens. Classification classes carry
 *  no colour of their own, so this is presentation only — nothing is stored. */
const accentFor = (index) => `var(--chart-${(index % 5) + 1})`;

/** Digits read left to right: 1-9 then 0 for the tenth. Beyond that, no key. */
const shortcutFor = (index) => {
    if (index < 9) return String(index + 1);
    if (index === 9) return '0';
    return null;
};

const ClassificationMiddleTop = () => {
    const { classes, handlers, selectedImages, selectedImage, unstructuredImages } =
        useClassificationLabelHook();
    const { t } = useI18n();
    const [aiOpen, setAiOpen] = useState(false);
    const groupable = canGroup(unstructuredImages);

    const bulkCount = selectedImages?.size ?? 0;
    const isBulk = bulkCount > 1;
    const labeled = classes.reduce((total, cls) => total + (cls.images?.length ?? 0), 0);
    const remaining = unstructuredImages?.length ?? 0;
    const total = labeled + remaining;
    const percent = total > 0 ? Math.round((labeled / total) * 100) : 0;

    const assign = (classId) => {
        if (isBulk) {
            handlers.assignMultipleToClass(classId);
        } else {
            handlers.handleClassButtonClick(classId);
        }
    };

    /*
     * No classes yet — which is EXACTLY the state the AI exists for.
     *
     * This early return used to precede the AI button entirely, so on a version
     * of 400 unsorted photos the screen offered only "set a count and hit
     * Create Classes": the user was told to make the very decision they wanted
     * the machine to make, and there was no AI affordance anywhere on the page.
     * Asking for "4 classes" here was not hard, it was impossible.
     */
    if (classes.length === 0) {
        return (
            <>
                <div className="lf-class-bar lf-class-bar--empty">
                    <i className="fa fa-layer-group" aria-hidden="true"></i>
                    <span>
                        <strong>{t('autoGroup.emptyTitle')}</strong>
                        {groupable ? t('autoGroup.emptyWithAi') : t('autoGroup.emptyManual')}
                    </span>
                    {groupable && (
                        <Button
                            className={`lf-tool-btn lf-class-bar__ai${aiOpen ? ' is-on' : ''}`}
                            onClick={() => setAiOpen((open) => !open)}
                            tooltipText={t('autoGroup.title')}
                        >
                            <i className="fa fa-magic" aria-hidden="true"></i>
                            <span className="lf-tool-btn__label">{t('autoGroup.open')}</span>
                        </Button>
                    )}
                </div>
                {aiOpen && <AutoGroupPanel onClose={() => setAiOpen(false)} />}
            </>
        );
    }

    return (
        <div className="lf-class-bar">
            <div className="lf-class-bar__status">
                {isBulk ? (
                    <div className="lf-class-bar__bulk">
                        <span className="lf-class-bar__bulk-count">{bulkCount} selected</span>
                        <span className="lf-class-bar__bulk-hint">
                            Pick a class to assign them all
                        </span>
                        <Button
                            className="lf-tool-btn lf-tool-btn--icon"
                            onClick={handlers.clearSelection}
                            tooltipText="Clear selection (Esc)"
                        >
                            <i className="fa fa-times" aria-hidden="true"></i>
                        </Button>
                        <Button
                            className="lf-tool-btn lf-tool-btn--icon lf-tool-btn--danger"
                            onClick={handlers.deleteSelectedImages}
                            tooltipText="Delete selected images"
                        >
                            <i className="fa fa-trash" aria-hidden="true"></i>
                        </Button>
                    </div>
                ) : (
                    <div className="lf-class-bar__progress">
                        <span className="lf-class-bar__progress-label">
                            <strong>{labeled}</strong> / {total} labeled
                            {remaining > 0 && <span className="lf-class-bar__remaining"> · {remaining} left</span>}
                        </span>
                        <span
                            className="lf-class-bar__progress-track"
                            role="progressbar"
                            aria-valuenow={percent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        >
                            <span className="lf-class-bar__progress-fill" style={{ width: `${percent}%` }} />
                        </span>
                    </div>
                )}

                {/*
                  * The AI the classification studio never had. Offered only when
                  * there is actually something to group, so it is not a button
                  * that explains why it cannot work.
                  */}
                {groupable && (
                    <Button
                        className={`lf-tool-btn lf-class-bar__ai${aiOpen ? ' is-on' : ''}`}
                        onClick={() => setAiOpen((open) => !open)}
                        tooltipText={t('autoGroup.title')}
                    >
                        <i className="fa fa-magic" aria-hidden="true"></i>
                        <span className="lf-tool-btn__label">{t('autoGroup.open')}</span>
                    </Button>
                )}
            </div>

            {aiOpen && <AutoGroupPanel onClose={() => setAiOpen(false)} />}

            <div className="lf-class-bar__grid">
                {classes.map((cls, index) => {
                    const shortcut = shortcutFor(index);
                    return (
                        <button
                            key={cls.id}
                            type="button"
                            className="lf-class-chip"
                            style={{ '--class-accent': accentFor(index) }}
                            onClick={() => assign(cls.id)}
                            disabled={!isBulk && !selectedImage}
                            title={
                                shortcut
                                    ? `Assign to ${cls.name} (${shortcut})`
                                    : `Assign to ${cls.name}`
                            }
                        >
                            {shortcut && <span className="lf-class-chip__key">{shortcut}</span>}
                            <span className="lf-class-chip__name">{cls.name}</span>
                            <span className="lf-class-chip__count">{cls.images?.length ?? 0}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ClassificationMiddleTop;
